import { Button } from "react-bootstrap";
import { ImCross, ImNext } from "react-icons/im";
import { IoIosCheckmarkCircle, IoMdSkipForward } from "react-icons/io";
import { BsCircleFill } from "react-icons/bs";
import { Question } from "../../types";
import { getSjtCorrectOrderIds } from "../../components/SJT/sjtUtils";
import { useEffect } from "react";
import { getEmqSidebarItemClassName } from "../../utils/emqSidebarGrouping";

const PracticePageSidebar = (props: {
  questions: Question[];
  submittedQuestionIds: Set<number>;
  selectedAnswers: {
    [key: number]: number;
  };
  // For SJT questions, ordered answer option ids by question id
  sjtOrderedAnswers?: {
    [key: number]: number[];
  };
  // For RolePlay questions, track if answered correctly
  rolePlayAnswers?: {
    [key: number]: boolean;
  };
  maxSeenQuestionIndex: number;
  currentQuestionIndex: number;
  onQuestionClicked: (index: number) => void;
  totalQuestionCount?: number;
  isLoadingMore?: boolean;
  sessionLimitReached?: boolean;
  sessionQuestionLimit?: number;
}) => {
  useEffect(() => {
    const element = document.getElementById(`q${props.currentQuestionIndex + 1}`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [props.currentQuestionIndex]);

  const questions = props.questions.slice(0, props.maxSeenQuestionIndex + 1);
  const isSjtQuestion = (q?: Question) => (
    q?.type?.toLowerCase?.() === 'sjt' ||
    (Array.isArray(q?.answerOptions) && q!.answerOptions.some(a => typeof a.correctRank === 'number'))
  );
  const isRolePlayQuestion = (q?: Question) => q?.type?.toLowerCase?.() === 'role_play';

  const hasSelectedAnswer = (q: Question) => {
    if (isRolePlayQuestion(q)) {
      return props.rolePlayAnswers?.[q.id] !== undefined;
    }
    if (isSjtQuestion(q)) {
      // SJT ranking is pre-filled on view; only submission reflects a committed answer
      return false;
    }
    return props.selectedAnswers[q.id] !== undefined;
  };

  const isQuestionCorrect = (q: Question) => {
    if (isRolePlayQuestion(q)) {
      return props.rolePlayAnswers?.[q.id] === true;
    }
    if (isSjtQuestion(q)) {
      const studentOrder = props.sjtOrderedAnswers?.[q.id];
      const correctOrder = getSjtCorrectOrderIds(q) || [];
      if (!studentOrder || studentOrder.length !== correctOrder.length) return false;
      return studentOrder.every((id, i) => id === correctOrder[i]);
    }
    const selectedAnswerOptionId = props.selectedAnswers[q.id];
    const correctAnswerOption = q.answerOptions?.find((answer) => answer.isCorrect);
    if (!correctAnswerOption) return false;
    return selectedAnswerOptionId === correctAnswerOption.id;
  };

  const numberCorrect = Array.from(props.submittedQuestionIds).filter((questionId) => {
    const q = questions.find((question) => question.id === questionId);
    if (!q || q.skipped) return false;
    return isQuestionCorrect(q);
  }).length;

  const numberSkipped = Array.from(props.submittedQuestionIds).filter((questionId) => {
    const q = questions.find((question) => question.id === questionId);
    return !!q?.skipped;
  }).length;

  const numberIncorrect = props.submittedQuestionIds.size - numberCorrect - numberSkipped;

  const gradedCount = props.submittedQuestionIds.size - numberSkipped;
  const percentage =
    Math.round((numberCorrect / gradedCount) * 100) || 0;

  const renderQuestionLabel = (questionNumber: number) => <>Q{questionNumber}</>;

  const questionButtons = questions.map((question, index) => {
    const isViewingThisQuestion = props.currentQuestionIndex === index;
    const questionNumber = index + 1;
    const emqClassName = getEmqSidebarItemClassName(questions, index);
    const showEmqLabel =
      emqClassName.includes("sidebar-q--emq-start") ||
      emqClassName.includes("sidebar-q--emq-only");
    const isLast = index === questions.length - 1;

    const buttonStyle = {
      fontWeight: isViewingThisQuestion ? "bold" : "normal",
      fontSize: isViewingThisQuestion ? "larger" : "small",
      transition: "all 0.3s ease",
      boxShadow: isViewingThisQuestion
        ? "0 1px 10px rgba(0, 0, 0, 0.1)"
        : undefined,
    } as const;

    const wrap = (button: JSX.Element) => (
      <li
        key={question.id}
        className={emqClassName || undefined}
        style={{ marginBottom: isLast && !emqClassName.includes("emq-end") ? 40 : undefined }}
      >
        {showEmqLabel ? <span className="sidebar-q__emq-label">EMQ</span> : null}
        {button}
      </li>
    );

    if (!props.submittedQuestionIds.has(question.id)) {
      const answered = hasSelectedAnswer(question);
      const skipped = !answered && !isViewingThisQuestion;

      let icon: JSX.Element;
      if (answered) {
        icon = (
          <BsCircleFill
            size={isViewingThisQuestion ? 12 : 10}
            color="var(--brand-accent, #1f4b7a)"
            style={{ marginRight: 2 }}
            title="Answered"
          />
        );
      } else if (skipped) {
        icon = (
          <IoMdSkipForward
            size={isViewingThisQuestion ? 16 : 14}
            style={{
              backgroundColor: "#ff9921",
              color: "white",
              padding: "3px",
              borderRadius: "10px",
            }}
          />
        );
      } else {
        icon = <ImNext size={isViewingThisQuestion ? 16 : 14} />;
      }

      return wrap(
        <Button
          id={`q${questionNumber}`}
          style={buttonStyle}
          onClick={() => {
            props.onQuestionClicked(index);
          }}
          size="sm"
          variant={isViewingThisQuestion ? "light" : "light"}
        >
          {icon} {renderQuestionLabel(questionNumber)}
        </Button>,
      );
    }

    const isCorrect = isQuestionCorrect(question);
    const isSkipped = !!question.skipped;

    if (isSkipped) {
      return wrap(
        <Button
          id={`q${questionNumber}`}
          style={buttonStyle}
          onClick={() => {
            props.onQuestionClicked(index);
          }}
          size="sm"
          variant={isViewingThisQuestion ? "light" : "light"}
        >
          <IoMdSkipForward
            size={isViewingThisQuestion ? 16 : 14}
            style={{
              backgroundColor: "#ff9921",
              color: "white",
              padding: "3px",
              borderRadius: "10px",
            }}
          />{" "}
          {renderQuestionLabel(questionNumber)}
        </Button>,
      );
    }

    if (isCorrect) {
      return wrap(
        <Button
          id={`q${questionNumber}`}
          style={buttonStyle}
          onClick={() => {
            props.onQuestionClicked(index);
          }}
          size="sm"
          variant={isViewingThisQuestion ? "light" : "light"}
        >
          <IoIosCheckmarkCircle
            size={isViewingThisQuestion ? 18 : 16}
            color="green"
          />{" "}
          {renderQuestionLabel(questionNumber)}
        </Button>,
      );
    }

    return wrap(
      <Button
        id={`q${questionNumber}`}
        style={buttonStyle}
        onClick={() => {
          props.onQuestionClicked(index);
        }}
        size="sm"
        variant={isViewingThisQuestion ? "light" : "light"}
      >
        <ImCross
          color="white"
          size={isViewingThisQuestion ? 15 : 14}
          style={{
            backgroundColor: "red",
            borderRadius: "10px",
            padding: "3px",
            marginRight: "5px",
          }}
        />
        {renderQuestionLabel(questionNumber)}
      </Button>,
    );
  });

  return (
    <div className="questions-progress-tracker">
      <div className="sticky-top-part">
        <h5 className="mb-4">
          Score <span style={{ float: "right" }}>{percentage}%</span>
        </h5>
        <ul>
          <li>
            <IoIosCheckmarkCircle color="green" size={20} /> Correct{" "}
            <span className="progress-total-correct">{numberCorrect}</span>
          </li>
          <li>
            <ImCross
              color="white"
              size={18}
              style={{
                backgroundColor: "red",
                borderRadius: "10px",
                padding: "5px",
              }}
            />{" "}
            Incorrect{" "}
            <span className="progress-total-incorrect">{numberIncorrect}</span>
          </li>
        </ul>
      </div>

      <div className="past-questions-tracker">
        <ul>{questionButtons}</ul>

        {props.isLoadingMore ? (
          <p style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>Loading more...</p>
        ) : null}

        {props.totalQuestionCount !== undefined &&
        props.totalQuestionCount > props.questions.length ? (
          <p style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>
            Showing {props.questions.length} of {props.totalQuestionCount}
          </p>
        ) : null}

        {props.sessionLimitReached ? (
          <p style={{ fontSize: 12, color: "#666", marginBottom: 20 }}>
            Session limit reached ({props.sessionQuestionLimit ?? 50} questions).
            {props.totalQuestionCount === undefined
              ? " Answer some questions to load more."
              : " Review these before loading more."}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default PracticePageSidebar;
